import { IconAddImage, IconAddYoutube, IconAlignCenter, IconAlignJustify, IconAlignLeft, IconAlignRight, IconBulletList, IconChecklist, IconCode, IconCodeBlock, IconHorizontalRule, IconLink, IconNumberedList, IconParagraph, IconQuote, IconRedo, IconTable, IconUndo } from '@repo/icons';
import { Control } from './Control';
import { addImage, addYoutubeVideo, setLink, setTable } from './utils';
import { ToolbarProps } from './utils';
import { createSignal, JSX } from 'solid-js';
import { Button } from '@repo/ui/button';

function Separator() {
  return (
    <div class="flex items-center" aria-hidden="true">
      <div class="h-full border-l border-gray-200" />
    </div>
  );
}

export function ToolbarContents(props: ToolbarProps): JSX.Element {
  const [value, setValue] = createSignal("");

  return (
    <div class="sticky top-0 left-0 z-10 flex flex-row flex-wrap p-2 gap-x-1 bg-gray-100 dark:bg-gray-800 mx-6 rounded-3xl">
      <Control
        key="undo"
        class=""
        editor={props.editor}
        onChange={() => props.editor.chain().focus().undo().run()}
        title="Undo (CTRL + Z)"
      >
        <IconUndo class="w-full h-full m-1" />
      </Control>
      <Control
        key="redo"
        class=""
        editor={props.editor}
        onChange={() => props.editor.chain().focus().redo().run()}
        title="Redo (CTRL + Y)"
      >
        <IconRedo class="w-full h-full m-1" />
      </Control>
      <Separator />
      <Control
        key="format-paragraph"
        class=""
        editor={props.editor}
        onChange={() => props.editor.chain().focus().setParagraph().run()}
        isActive={editor => editor.isActive('paragraph')}
        title="Paragraph"
      >
        <IconParagraph class="w-full h-full m-1" />
      </Control>
      <Control
        key="heading-1"
        class="font-bold text-sm"
        editor={props.editor}
        onChange={() => props.editor.chain().focus().toggleHeading({ level: 1 }).run()}
        isActive={editor => editor.isActive('heading', { level: 1 })}
        title="Heading 1"
      >
        H1
      </Control>
      <Control
        key="heading-2"
        class="font-bold text-sm"
        editor={props.editor}
        onChange={() => props.editor.chain().focus().toggleHeading({ level: 2 }).run()}
        isActive={editor => editor.isActive('heading', { level: 2 })}
        title="Heading 2"
      >
        H2
      </Control>
      <Control
        key="heading-3"
        class="font-bold text-sm"
        editor={props.editor}
        onChange={() => props.editor.chain().focus().toggleHeading({ level: 3 }).run()}
        isActive={editor => editor.isActive('heading', { level: 3 })}
        title="Heading 3"
      >
        H3
      </Control>
      <Separator />
      <Control
        key="bold"
        class="font-bold text-sm"
        editor={props.editor}
        onChange={() => props.editor.chain().focus().toggleBold().run()}
        isActive={editor => editor.isActive('bold')}
        title="Bold (CTRL + B)"
      >
        B
      </Control>
      <Control
        key="italic"
        class="italic text-sm"
        editor={props.editor}
        onChange={() => props.editor.chain().focus().toggleItalic().run()}
        isActive={editor => editor.isActive('italic')}
        title="Italic (CTRL + I)"
      >
        I
      </Control>
      <Control
        key="underline"
        class="decoration-solid text-sm underline"
        editor={props.editor}
        onChange={() => props.editor.chain().focus().toggleUnderline().run()}
        isActive={editor => editor.isActive('underline')}
        title="Underline (CTRL + U)"
      >
        U
      </Control>
      <Control
        key="strike"
        class="line-through text-sm"
        editor={props.editor}
        onChange={() => props.editor.chain().focus().toggleStrike().run()}
        isActive={editor => editor.isActive('strike')}
        title="Strike Through"
      >
        S
      </Control>
      <Control
        key="superscript"
        class="text-sm"
        editor={props.editor}
        onChange={() => props.editor.chain().focus().toggleSuperscript().run()}
        isActive={editor => editor.isActive('superscript')}
        title="Superscript"
      >
        A<sup>2</sup>
      </Control>
      <Control
        key="subscript"
        class="text-sm"
        editor={props.editor}
        onChange={() => props.editor.chain().focus().toggleSubscript().run()}
        isActive={editor => editor.isActive('subscript')}
        title="Subscript"
      >
        A<sub>2</sub>
      </Control>
      <Separator />
      <Control
        key="bulletList"
        class=""
        editor={props.editor}
        onChange={() => props.editor.chain().focus().toggleBulletList().run()}
        isActive={editor => editor.isActive('bulletList')}
        title="Bullet List"
      >
        <IconBulletList class="w-full h-full m-1" />
      </Control>
      <Control
        key="orderedList"
        class=""
        editor={props.editor}
        onChange={() => props.editor.chain().focus().toggleOrderedList().run()}
        isActive={editor => editor.isActive('orderedList')}
        title="Ordered List"
      >
        <IconNumberedList class="w-full h-full m-1" />
      </Control>
      <Control
        key="taskList"
        class=""
        editor={props.editor}
        onChange={() => props.editor.chain().focus().toggleTaskList().run()}
        isActive={editor => editor.isActive('taskList')}
        title="Task List"
      >
        <IconChecklist class="w-full h-full m-1" />
      </Control>
      <Control
        key="horizontalRule"
        class=""
        editor={props.editor}
        onChange={() => props.editor.chain().focus().setHorizontalRule().run()}
        title="Horizontal Rule"
      >
        <IconHorizontalRule class="w-full h-full m-1" />
      </Control>
      <Separator />
      <Control
        key="code"
        class=""
        editor={props.editor}
        onChange={() => props.editor.chain().focus().toggleCode().run()}
        isActive={editor => editor.isActive('code')}
        title="Code"
      >
        <IconCode class="w-full h-full m-1" />
      </Control>
      <Control
        key="codeBlock"
        class=""
        editor={props.editor}
        onChange={() => props.editor.chain().focus().toggleCodeBlock().run()}
        isActive={editor => editor.isActive('codeBlock')}
        title="Code Block"
      >
        <IconCodeBlock class="w-full h-full m-1" />
      </Control>
      <Control
        key="blockquote"
        class=""
        editor={props.editor}
        onChange={() => props.editor.chain().focus().toggleBlockquote().run()}
        isActive={editor => editor.isActive('blockquote')}
        title="Blockquote"
      >
        <IconQuote class="w-full h-full m-1" />
      </Control>
      <Separator />
      <Control
        key="link"
        class="text-sm"
        editor={props.editor}
        onChange={() => null}
        isActive={editor => editor.isActive('link')}
        title="Link"
        popoverTarget='popover-link'
      >
        <IconLink class="w-full h-full m-1" />
      </Control>
      <Control
        key="image"
        class=""
        editor={props.editor}
        onChange={() => null}
        title="Insert Image"
        popoverTarget='popover-image'
      >
        <IconAddImage class="w-full h-full m-1" />
      </Control>
      <Control
        key="youtube"
        class=""
        editor={props.editor}
        onChange={() => null}
        title="Insert Youtube Video"
        popoverTarget='popover-youtube'
      >
        <IconAddYoutube class="w-full h-full m-1" />
      </Control>
      <Control
        key="Table"
        class=""
        editor={props.editor}
        onChange={() => setTable(props.editor)}
        title=" Insert Table"
      >
        <IconTable class="w-full h-full m-1" />
      </Control>
      <Separator />
      <Control
        key="Text Align Left"
        class=""
        editor={props.editor}
        onChange={() => props.editor.chain().focus().setTextAlign('left').run()}
        isActive={editor => editor.isActive({ textAlign: 'left' })}
        title="Text Align Left"
      >
        <IconAlignLeft class="w-full h-full m-1" />
      </Control>
      <Control
        key="Text Align Center"
        class=""
        editor={props.editor}
        onChange={() => props.editor.chain().focus().setTextAlign('center').run()}
        isActive={editor => editor.isActive({ textAlign: 'center' })}
        title="Text Align Center"
      >
        <IconAlignCenter class="w-full h-full m-1" />
      </Control>
      <Control
        key="Text Align Right"
        class=""
        editor={props.editor}
        onChange={() => props.editor.chain().focus().setTextAlign('right').run()}
        isActive={editor => editor.isActive({ textAlign: 'right' })}
        title="Text Align Right"
      >
        <IconAlignRight class="w-full h-full m-1" />
      </Control>
      <Control
        key="Text Align Justify"
        class=""
        editor={props.editor}
        onChange={() => props.editor.chain().focus().setTextAlign('justify').run()}
        isActive={editor => editor.isActive({ textAlign: 'justify' })}
        title="Text Align Justify"
      >
        <IconAlignJustify class="w-full h-full m-1" />
      </Control>

      {
        //popovers
      }
      <div id="popover-youtube" popover class="fixed left-1/2 -translate-x-1/2 bg-gray-100 text-gray-950 dark:text-gray-50 dark:bg-gray-950 border border-gray-500 dark:border dark:border-gray-100 p-6 rounded-lg shadow-lg w-80">
        <Button variant="ghost" size="xs" popovertarget="popover-youtube" popovertargetaction="hide" class="absolute top-2 right-2 hover:bg-gray-100 dark:hover:bg-gray-950">
          ❌
        </Button >
        <p class="text-lg mb-4">Embed Youtube Video</p>
        <hr class="border-t-1 border-gray-500 dark:border-gray-100 mb-4" />
        <input
          type="text"
          value={value()}
          onInput={(e) => setValue(e.currentTarget.value)}
          class="w-full p-2 border rounded mb-4 bg-gray-50 dark:bg-gray-950"
        />
        <div class="flex justify-end space-x-2">
          <Button variant="outline"
            popoverTarget='popover-youtube'
            popoverTargetAction='hide'
            onClick={() => setValue("")}
            class="border border-gray-900 hover:bg-gray-200 dark:hover:bg-gray-500 dark:border-gray-300 rounded p-2 px-4"
          >
            Cancel
          </Button >
          <Button variant="outline"
            popoverTarget='popover-youtube'
            popoverTargetAction='hide'
            class="bg-gray-200 hover:bg-gray-300 dark:bg-gray-900 dark:hover:bg-gray-600 rounded-sm p-2 px-4"
            onClick={() => {
              value() != "" ? addYoutubeVideo(props.editor, value()) : null
              setValue("")
            }}
          >
            OK
          </Button >
        </div>
      </div>

      <div id="popover-image" popover class="fixed left-1/2 -translate-x-1/2 bg-gray-100 text-gray-950 dark:text-gray-50 dark:bg-gray-950 border border-gray-500 dark:border dark:border-gray-100 p-6 rounded-lg shadow-lg w-80">
        <Button variant="ghost" size="xs" popovertarget="popover-image" popovertargetaction="hide" class="absolute top-2 right-2 hover:bg-gray-100 dark:hover:bg-gray-950">
          ❌
        </Button >
        <p class="text-lg mb-4">Insert Image</p>
        <hr class="border-t-1 border-gray-500 dark:border-gray-100 mb-4" />
        <input
          type="text"
          value={value()}
          onInput={(e) => setValue(e.currentTarget.value)}
          class="w-full p-2 border rounded mb-4 dark:bg-gray-950"
        />
        <div class="flex justify-end space-x-2">
          <Button
            variant="outline"
            popoverTarget='popover-image'
            popoverTargetAction='hide'
            onClick={() => setValue("")}
            class="border border-gray-900 hover:bg-gray-200 dark:hover:bg-gray-500 dark:border-gray-300 rounded p-2 px-4"
          >
            Cancel
          </Button >
          <Button
            variant="outline"
            popoverTarget='popover-image'
            popoverTargetAction='hide'
            class="bg-gray-200 hover:bg-gray-300 dark:bg-gray-900 dark:hover:bg-gray-600 rounded-sm p-2 px-4"
            onClick={() => {
              value() != "" ? addImage(props.editor, value()) : null
              setValue("")
            }}
          >
            OK
          </Button >
        </div>
      </div>

      <div id="popover-link" popover class="fixed left-1/2 -translate-x-1/2 bg-gray-100 text-gray-950 dark:text-gray-50 dark:bg-gray-950 border border-gray-500 dark:border dark:border-gray-100 p-6 rounded-lg shadow-lg w-80">
        <Button variant="ghost" size="xs" popovertarget="popover-link" popovertargetaction="hide" class="absolute top-2 right-2 hover:bg-gray-100 dark:hover:bg-gray-950">
          ❌
        </Button >
        <p class="text-lg mb-4">Insert Link</p>
        <hr class="border-t-1 border-gray-500 dark:border-gray-100 mb-4" />
        <input
          type="text"
          value={value()}
          onInput={(e) => setValue(e.currentTarget.value)}
          class="w-full p-2 border rounded mb-4 dark:bg-gray-950"
        />
        <div class="flex justify-end space-x-2">
          <Button variant="outline"
            popoverTarget='popover-link'
            popoverTargetAction='hide'
            class="border border-gray-900 hover:bg-gray-200 dark:hover:bg-gray-500 dark:border-gray-300 rounded p-2 px-4"
            onClick={() => setValue("")}
          >
            Cancel
          </Button >
          <Button variant="outline"
            popoverTarget='popover-link'
            popoverTargetAction='hide'
            class="bg-gray-200 hover:bg-gray-300 dark:bg-gray-900 dark:hover:bg-gray-600 rounded-sm p-2 px-4"
            onClick={() => {
              setLink(props.editor, value())
              setValue("")
            }}
          >
            OK
          </Button >
        </div>
      </div>
    </div>
  );
}
